import { CoreButton, CoreInput, CoreModalDialogFooter } from '@components'
import { toastSuccess } from '@utils'
import { useCreateCounterpartyMutation, useUpdateCounterpartyMutation } from 'mmfintech-backend-api'
import type { CounterpartyRequest, CounterpartyResponse } from 'mmfintech-backend-api/types'
import { isValidObject, tr, useFormValues } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { type FC, type FormEvent, useEffect } from 'react'

import './counterpartyCreateModal.scss'

interface CounterpartyCreateModalProps {
  onClose: () => void
  counterparty?: CounterpartyResponse
}

export const CounterpartyCreateModal: FC<CounterpartyCreateModalProps> = ({ onClose, counterparty }) => {
  const [createCounterpartyRequest, { isLoading: loadingCreate, reset: resetCreate, error: createError }] =
    useCreateCounterpartyMutation()
  const [updateCounterpartyRequest, { isLoading: loadingUpdate, reset: resetUpdate, error: updateError }] =
    useUpdateCounterpartyMutation()

  const formValues = useFormValues({
    name: {
      required: true,
      validation: 'safe-string',
      value: counterparty?.name,
      minLength: 4,
      maxLength: 100
    }
  })

  const handleReset = () => {
    resetCreate()
    resetUpdate()
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (formValues.areValid()) {
      const data = formValues.prepare() as CounterpartyRequest

      try {
        const res = counterparty
          ? await updateCounterpartyRequest({
              ...data,
              counterpartyId: counterparty?.id
            }).unwrap()
          : await createCounterpartyRequest(data).unwrap()
        if (res) {
          toastSuccess(tr('FRONTEND.REPORTS.ACTIONS.COUNTERPARTY_CREATED', 'Counterparty successfully created'))
          onClose()
        }
      } catch (error) {
        formValues.handleErrors(error)
      }
    }
  }

  useEffect(() => {
    handleReset()
  }, [formValues.getValue('name')])

  useEffect(() => {
    return () => {
      handleReset()
    }
  }, [])

  return (
    <div className='counterparty-modal-wrapper'>
      <form onSubmit={handleSubmit}>
        <CoreInput
          type='text'
          label={tr('FRONTEND.COUNTERPARTY.COLUMN_NAME', 'Name')}
          {...formValues.registerInput('name')}
        />
        <ErrorDisplay error={[createError, updateError]} />
        <CoreModalDialogFooter>
          <CoreButton
            type='submit'
            data-test='create-update-counterpary'
            variation='primary-black'
            text={
              isValidObject(counterparty)
                ? tr('FRONTEND.COUNTERPARTY.ACTION.UPDATE', 'Update')
                : tr('FRONTEND.COUNTERPARTY.ACTION.CREATE', 'Create')
            }
            isLoading={loadingCreate || loadingUpdate}
            size='large'
            fullWidth
          />
        </CoreModalDialogFooter>
      </form>
    </div>
  )
}
